import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { ShopState, FetchShops, SetActiveShop } from '../../_state/shop/shop.state';
import { Shop } from '../../_state/shop/shop.model';
import { AgencyStateService } from '../services/agency-state.service';

/**
 * Guard for `/agency/shops/:shopId/*` routes.
 *
 * Reads `:shopId` from the URL, finds the matching shop in `ShopState.shops`
 * (fetching the list first if it isn't loaded yet), and dispatches
 * `SetActiveShop(shop)` so the brand-page components downstream (Dashboard,
 * Sales & Profit, etc.) render data for that shop — without the user having
 * to leave the agency sidebar context.
 *
 * If `:shopId` is missing or doesn't resolve to a shop the user can access,
 * the guard redirects to `/agency/clients` rather than letting the brand
 * page render with stale/wrong active-shop state. (bd-8048)
 *
 * Returns a UrlTree (rather than `router.navigate(...) + return false`) so
 * that the redirect target is computed from the IN-FLIGHT URL — important
 * because `state.url` carries the `/demo` prefix while `Router.url` is
 * still the previous page during navigation. (bd-1utn)
 */
export const agencyShopContextGuard: CanActivateFn = async (route, state) => {
  const store = inject(Store);
  const router = inject(Router);

  // Compute the fallback once; reused by every error path below. Honours
  // the `/demo` prefix so guard-driven fallbacks don't punch users out of
  // the demo subtree.
  const fallback = () =>
    router.parseUrl(AgencyStateService.demoAwareUrl(state.url, '/agency/clients'));

  const shopId = route.paramMap.get('shopId');
  if (!shopId) {
    return fallback();
  }

  // Ensure shops are loaded — on a hard reload directly to
  // `/agency/shops/:id/dashboard`, the store may still be empty.
  let shops = store.selectSnapshot(ShopState.shops);
  if (!shops?.length) {
    try {
      await firstValueFrom(store.dispatch(new FetchShops()));
      shops = store.selectSnapshot(ShopState.shops);
    } catch {
      return fallback();
    }
  }

  const shop: Shop | undefined = shops.find(s => s.id === shopId);
  if (!shop) {
    return fallback();
  }

  // Only re-dispatch if the active shop is actually changing — avoids
  // resetting downstream state on intra-shop navigation (Dashboard → Sales).
  const current = store.selectSnapshot(ShopState.activeShop);
  if (current?.id !== shop.id) {
    await firstValueFrom(store.dispatch(new SetActiveShop(shop)));
  }

  return true;
};
